import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Grid, Typography, useMediaQuery } from '@mui/material';
import { YellowButton } from '@/components/button/button';
import DeskTopView from './components/desktop-view';
import MobileView from './components/mobile-view';
import { Link } from '@/components/link/link';
import Image from 'next/image';
import { nextImageTransitionProps } from '@/constants/next-image-transition-props';
import { getDiscoverExperiencesContent } from './fetch-content';
import { DiscoverExperienceDocument } from './types';
import { config } from '@/config/environment';

const MainDiv = styled.div`
  width: 100%;
  height: auto;
  margin: 0;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  justify-content: space-around;
  & a {
    :hover {
      text-decoration: none;
    }
  }
`;
const Row = styled(Grid)`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 50px;
  padding: 0 12.5%;
  @media (max-width: 768px) {
    padding: 0;
  }
`;
const Title = styled(Typography)`
  font-family: var(--font-cooper-light), sans-serif;
  font-size: 50px;
  font-weight: 300;
  line-height: 109.3%;
  color: #ffffff;
  text-align: start;
  margin: 0;
  z-index: 1;
  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 32px;
  }
`;
const Div = styled(Grid)`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${Row} {
    height: 57vh;
    max-height: 70vw;
    padding: 0;
  }
  ${Title} {
    width: 370px;
    @media (max-width: 768px) {
      width: 100%;
    }
  }
  @media (max-width: 768px) {
    height: auto;
    ${Row} {
      height: auto;
      max-height: max-content;
    }
  }
`;
const SecondDiv = styled(Grid)`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: #ffffff;
  margin-bottom: 100px;
  @media (max-width: 768px) {
    padding: 0 20px;
    width: 100%;
    margin-bottom: 0;
  }
`;
const ThirdDiv = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  margin-top: 50px;
  padding-bottom: 150px;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.palette.common.midnight};
  @media (max-width: 768px) {
    padding: 0 20px 120px 20px;
    width: 100%;
  }
`;
const ContentDiv = styled(Grid)`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 0;
  padding: 14.23% 12.5% 0 12.5%;
  background: ${({ theme }) => theme.palette.common.midnight};
  @media (max-width: 768px) {
    padding: 0 20px;
    width: 100%;
  }
`;
const TextDiv = styled(Grid)`
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding-left: 64px;
  @media (max-width: 768px) {
    padding: 0;
    width: 100%;
    margin-top: 0;
  }
`;
const TitleDiv = styled(Grid)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  @media (max-width: 768px) {
    padding: 0;
    width: 100%;
    margin-top: 32px;
  }
`;
const ExperienceImage = styled(Image)`
  width: 27.08%;
  min-width: 390px;
  height: auto;
  position: relative;
  @media (max-width: 768px) {
    width: 100%;
    height: auto;
  }
`;
ExperienceImage.defaultProps = {
  ...nextImageTransitionProps,
  style: undefined,
};
const SubTitle = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: 500;
  font-size: 28px;
  line-height: 127.3%;
  color: #ffffff;
  margin-bottom: 32px;
`;
const Text = styled(Typography)`
  font-family: var(--font-work-sans), sans-serif;
  font-size: 18px;
  line-height: 159.3%;
  color: #ffffff;
  min-width: 490px;
  z-index: 1;
  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
  }
`;
const SubText = styled(Text)`
  font-size: 16px;
  max-width: 768px;
  @media (max-width: 768px) {
    width: 100%;
  }
`;
const UnlistedText = styled(SubText)`
  margin-top: 60px;
`;

const Vector = styled.img`
  left: 0;
  top: 0;
  width: 50%;
  height: auto;
  object-fit: cover;
  position: absolute;
`;
const ExperienceTitle = styled(SubTitle)`
  color: #000000;
  margin-top: 80px;
  @media (max-width: 768px) {
    margin-top: 0;
  }
`;
const ExperienceDetail = styled(Text)`
  font-size: 16px;
  color: #000000;
  margin-top: 80px;
  @media (max-width: 768px) {
    margin-top: 0;
  }
`;
const BottomTitleDiv = styled(Grid)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin-top: 120px;
`;

const BottomTextDiv = styled(Grid)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  margin: 32px 0;
`;
const Wave = styled.div`
  display: inline-block;
  height: 100px;
  position: relative;
  width: 100%;
  z-index: 1;
  overflow: visible;
  svg {
    display: inline-block;
    position: absolute;
    top: -1px;
    left: 0;
    padding-left: -20px;
  }
`;
const DiscoverExperience = () => {
  const isMobile = useMediaQuery('(max-width:960px)');
  const [content, setContent] = useState<DiscoverExperienceDocument | null>(null);
  const archiveMode = config.platformMode === 'archive';

  useEffect(() => {
    const fetchContent = async () => {
      const content = await getDiscoverExperiencesContent();
      setContent(content);
    };
    fetchContent();
  }, []);

  if (!content) {
    return <div></div>;
  }

  const displayContent = archiveMode
    ? {
        ...content,
        section1: {
          title: 'What was a Curiko experience?',
          text: 'Curiko experiences were opportunities to connect with yourself, other people, and the world around you. Community members chose what they did, when they did it, and how.',
        },
        section2: {
          title: 'Why were Curiko experiences different?',
          subTextList: [
            'People joined experiences for many reasons:',
            '(1) Meet all kinds of different people',
            "(2) Spark a new interest by doing something you've never tried before",
            '(3) Gain skills and perspective through group or one-on-one coaching',
            '(4) Discover new opportunities through meaningful experiences',
            '(5) Spend time with people in ways that felt accessible and welcoming',
            '(6) Find more moments of connection—big or small',
          ],
        },
        section3: {
          title: 'Heaps of experiences!',
          detailText:
            'Every Curiko experience was hosted by a fellow community member with the intention of opening doors to new mindsets, friendships, groups, and pursuits. Experiences took place in three different formats.',
          experiences: content.section3?.experiences,
        },
        section4: {
          title: 'Who Went On Curiko experiences?',
          text: 'Experiences were for everyone, with and without disabilities. Curiko supported community members in turning their interests into accessible online and in-person experiences, while helping people build relationships and try new things.',
          buttonLabel: content.section4?.buttonLabel,
          buttonLink: content.section4?.buttonLink,
        },
      }
    : content;
  const { section1, section2, section3, section4 } = displayContent;

  return (
    <MainDiv>
      <Div container>
        {!isMobile && <Vector src="/discover/experiences/vector.png" />}
        <ContentDiv>
          <Row container>
            {isMobile && (
              <TitleDiv item xs={12}>
                <ExperienceImage
                  src={'/discover/experiences/bundle-mobile.png'}
                  alt={'bundle experience'}
                />
              </TitleDiv>
            )}
            <TitleDiv item md={6} xs={12}>
              <Title>{section1?.title}</Title>
            </TitleDiv>
            <TextDiv item md={6} xs={12}>
              <Text>{section1?.text}</Text>
            </TextDiv>
          </Row>
          <Row>
            <TitleDiv item md={6} xs={12}>
              <SubTitle>{section2?.title}</SubTitle>
              {section2?.subTextList?.map((subText, index) => (
                <SubText key={index}>{subText}</SubText>
              ))}
              <UnlistedText>{section2?.unlistedText}</UnlistedText>
            </TitleDiv>
            {!isMobile && (
              <TextDiv item md={6} xs={12}>
                <ExperienceImage
                  src={'/discover/experiences/bundle.png'}
                  alt={'bundle experience'}
                />
              </TextDiv>
            )}
          </Row>
        </ContentDiv>
      </Div>
      <Wave>
        <svg
          width="100%"
          height="92"
          viewBox="0 0 1440 92"
          fill="none"
          preserveAspectRatio={isMobile ? 'xMidYMin slice' : 'none'}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M615 47.7189C333 47.7189 158 135.855 0 63.4406V0.5H1450V17.2284C1450 17.2284 1522.98 37.6181 1206.5 76.7802C937 110.129 752.26 47.7189 615 47.7189Z"
            fill="#231727"
          />
        </svg>
      </Wave>
      <SecondDiv>
        <Row container>
          <TitleDiv item md={6} xs={12}>
            <ExperienceTitle>{section3?.title}</ExperienceTitle>
          </TitleDiv>
          <TextDiv item md={6} xs={12}>
            <ExperienceDetail>{section3?.detailText}</ExperienceDetail>
          </TextDiv>
        </Row>
        {isMobile ? (
          <MobileView />
        ) : (
          <DeskTopView experiences={section3?.experiences} archiveMode={archiveMode} />
        )}
      </SecondDiv>
      <Wave>
        <svg
          width="100%"
          height="183"
          viewBox="0 0 1440 183"
          fill="none"
          preserveAspectRatio={isMobile ? 'xMinYMin slice' : 'none'}
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginTop: '0px' }}
        >
          <path
            d="M726.571 83.782C1103.09 -78.6089 1315.46 40.5183 1439 83.7821V182L-14.5 182V83.782C297.316 83.782 435.359 209.381 726.571 83.782Z"
            fill="#231727"
          />
        </svg>
      </Wave>
      <ThirdDiv>
        <BottomTitleDiv item md={6} xs={12}>
          <SubTitle>{section4?.title} </SubTitle>
        </BottomTitleDiv>
        <BottomTextDiv item md={6} xs={12}>
          <SubText>{section4?.text}</SubText>
        </BottomTextDiv>
        <Link href={section4?.buttonLink || ''}>
          <YellowButton label={section4?.buttonLabel} />
        </Link>
      </ThirdDiv>
    </MainDiv>
  );
};

export default DiscoverExperience;
